import React, { useRef, useEffect, useCallback } from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'

// Utils
import { usePrevious } from '@sketch/utils'
import {
  createSrcSetForFiles,
  artboardContainerCursor,
  scrollOffset,
  shrinkToFitFactor,
  avoidBrowserBackActionWhenPanning,
} from './utils'
import { getArtboardThumbnail } from 'modules/shares/utils/getArtboardThumbnail'

// Components
import { ZoomTypes, ZoomProps } from 'components/Zoom'
import { OverlayGrid } from 'modules/shares/components/OverlayGrid'
import { OverlayLayout } from 'modules/shares/components/OverlayLayout'
import ArtboardPaginationControls from '../ArtboardPaginationControls'
import { ArtboardAnnotationOverlay } from 'modules/annotations/components'

// Hooks
import useScaleArtboard from './useScaleArtboard'
import { useVersioning } from 'modules/versioning'
import {
  useActiveAnnotation,
  useAnnotationOverlayOrigin,
  useCreateDraftAnnotation,
  useMovingAnnotation,
  usePlaceDraftAnnotation,
} from 'modules/annotations/hooks'
import { useComponentsDescriptionContext } from 'modules/shares/ComponentsWebView/context'

import { DraftCommentProvider } from 'modules/annotations/components/DraftCommentContext'

// Styles
import {
  Container,
  ScrollArea,
  ArtboardContainer,
  ArtboardImage,
  ArtboardInspector,
} from './ArtboardView.styles'

// Types
import { InspectorState } from 'modules/shares/Inspector/types'
import {
  ArtboardDetailInfoFragment,
  VersionFragment,
  useGetPendingDescriptionQuery,
} from '@sketch/gql-types'
import { Pagination } from '../../types'
import { useFlag } from 'containers/flags'
import { IS_EMBEDDED } from '@sketch/constants'

type Artboard = Pick<
  ArtboardDetailInfoFragment,
  | 'width'
  | 'height'
  | 'files'
  | 'isPage'
  | 'name'
  | 'hasMissingFonts'
  | 'uuid'
  | 'component'
>

export type ArtboardFile = NonNullable<NonNullable<Artboard['files']>[0]>

interface ArtboardViewProps extends RouteComponentProps {
  currentVersion?: VersionFragment
  artboard?: Artboard
  pagination: Pagination | undefined
  currentZoom: ZoomTypes
  onZoomChanged: ZoomProps['onZoomChanged']
  inspector: InspectorState
  shareIdentifier: string
  permanentArtboardShortId?: string
  permanentPageIdentifier: string
  onContainerClick?: () => void
}

// Globals
const IS_SAFARI = window.safari !== undefined
const FIT_TO_SCREEN_PADDING = 20

const ArtboardView = ({
  artboard,
  pagination,
  currentZoom,
  onZoomChanged,
  inspector,
  shareIdentifier,
  permanentArtboardShortId,
  permanentPageIdentifier,
  onContainerClick,
  location,
  history,
  ...rest
}: ArtboardViewProps) => {
  const isComponentDescriptionsOn = useFlag('components-description')

  const isValidPagination =
    Number.isInteger(pagination?.current) && Number.isInteger(pagination?.total)

  const scrollRef = useRef<HTMLDivElement>()
  const scroll = useRef({ top: 0, left: 0 })
  const previousZoom = usePrevious(currentZoom)
  const zoomPoint = useRef({
    top: 0,
    left: 0,
  })

  const { artboardThumbnail, ...versioning } = useVersioning()
  const currentVersion = !versioning.loading ? versioning.currentVersion : null

  const {
    componentsDescription,
    onComponentSelected,
  } = useComponentsDescriptionContext()

  const {
    data: pendingDescriptionsData,
    loading: pendingDescriptionsLoading,
  } = useGetPendingDescriptionQuery({
    variables: {
      shareIdentifier,
      versionIdentifier: currentVersion?.identifier!,
    },
    skip: !currentVersion?.identifier || !isComponentDescriptionsOn,
  })

  // Sync description or pending description with Component Description context
  // The logic way to deal with this effect would be to call
  // `resetComponentDescriptionSelection` when the component is unmounted, but
  // this is causing an inifinte loop
  useEffect(() => {
    if (
      pendingDescriptionsLoading ||
      !currentVersion?.identifier ||
      componentsDescription.selected === artboard?.uuid ||
      !isComponentDescriptionsOn
    ) {
      return
    }

    if (artboard?.uuid) {
      const editingComponent = (
        pendingDescriptionsData?.collaborativeEditingSession.componentEdits ||
        []
      ).find(
        component =>
          (component.componentUuid as string).toUpperCase() === artboard?.uuid
      )

      const componentDescription = {
        componentUuid: artboard?.uuid,
        documentVersion: currentVersion?.document?.documentVersion!,
        baseVersionIdentifier: currentVersion?.identifier!,
        compatibilityVersion: currentVersion?.document
          ?.coeditCompatibilityVersion!,
        shareIdentifier,
        canEditDescriptions: currentVersion?.document?.canEditDescriptions!,
        description:
          editingComponent?.description || artboard?.component?.description,
      }

      onComponentSelected(artboard?.uuid, componentDescription)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    artboard?.uuid,
    artboard?.component?.description,
    shareIdentifier,
    pendingDescriptionsLoading,
    currentVersion?.identifier,
    componentsDescription.selected,
    isComponentDescriptionsOn,
  ])

  const artboardSize = {
    width: artboard?.width ?? 0,
    height: artboard?.height ?? 0,
  }

  const [measureRef, scaledArtboardSize, containerSize] = useScaleArtboard({
    artboardSize,
    currentZoom,
    onZoomChanged,
  })

  const containerRef = useCallback(
    (element: HTMLDivElement) => {
      scrollRef.current = element
      measureRef(element)

      if (element !== null && !IS_SAFARI) {
        element.addEventListener('wheel', avoidBrowserBackActionWhenPanning)

        return () => {
          element.removeEventListener(
            'wheel',
            avoidBrowserBackActionWhenPanning
          )
        }
      }
    },
    [scrollRef, measureRef]
  )

  const artboardImageRef = useRef<HTMLDivElement>(null)
  const [placeDraftAnnotation] = usePlaceDraftAnnotation() || []
  const handleDraftAnnotationEvent = useCreateDraftAnnotation({
    shareIdentifier,
    permanentPageId: permanentPageIdentifier,
    permanentArtboardId: artboard?.uuid || '',
  })

  const [
    annotationOverlayOrigin /* eslint-disable-line @typescript-eslint/no-unused-vars */,
    setAnnotationOverlayOrigin,
  ] = useAnnotationOverlayOrigin()
  useEffect(() => {
    setAnnotationOverlayOrigin(artboardImageRef, currentZoom.factor || 0)
  }, [currentZoom, setAnnotationOverlayOrigin])

  const [activeAnnotation, setActiveAnnotation] = useActiveAnnotation()
  const [movingAnnotation] = useMovingAnnotation() || []

  useEffect(() => {
    if (!scroll.current || !scrollRef.current) {
      return
    }

    scrollRef.current.scrollTop = scroll.current.top
    scrollRef.current.scrollLeft = scroll.current.left
  }, [scroll, scrollRef, currentZoom])

  const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (inspector.isActive) {
      inspector.onClickLayer(undefined)
      return
    } else if (activeAnnotation) {
      /**
       * If the container if clicked and there's an active annotation
       * we should hide it
       */
      setActiveAnnotation()
    } else if (placeDraftAnnotation) {
      return
    }
  }

  const handleContainerMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    if (movingAnnotation) {
      return
    } else if (placeDraftAnnotation) {
      return
    } else if (inspector.isActive) {
      return
    } else {
      if (currentZoom.type === 'ZOOM_TO_FIT' && currentZoom.factor === 1) {
        return
      }

      if (currentZoom.type !== 'ZOOM_TO_FIT') {
        onZoomChanged({ type: 'ZOOM_TO_FIT' })
      } else {
        const target = event.currentTarget as HTMLElement
        const clickOffset = {
          top: event.clientY - target.getBoundingClientRect().top,
          left: event.clientX - target.getBoundingClientRect().left,
        }

        zoomPoint.current = {
          top: clickOffset.top,
          left: clickOffset.left,
        }
        onZoomChanged({ type: 'ZOOM_FACTOR', factor: 1 })
      }
    }
  }

  const files = (artboard?.files.filter(x => !!x) as ArtboardFile[]) || []

  const zoomToFitFactor = shrinkToFitFactor({
    containerSize: {
      width: containerSize.width - FIT_TO_SCREEN_PADDING * 2,
      height: containerSize.height - FIT_TO_SCREEN_PADDING * 2,
    },
    itemSize: artboardSize,
  })

  if (
    scrollRef.current &&
    typeof currentZoom.factor === 'number' &&
    typeof previousZoom!.factor === 'number'
  ) {
    const scaleFactor = currentZoom.factor / previousZoom!.factor

    scroll.current = scrollOffset({
      scaleFactor,
      scaledSize: scaledArtboardSize,
      containerSize,
      currentOffset: scroll.current,
      centerPoint: zoomPoint.current,
    })

    zoomPoint.current = {
      top: 0,
      left: 0,
    }
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event && (event.target as Element)
    if (!target || currentZoom.type === 'ZOOM_TO_FIT') {
      return
    }

    // Target will return scroll left as 0 when zooming in the artboard
    // which impacts how the scrollOffset is calculated so we're
    // using the last scroll value in those cases.
    const scrollTop = target.scrollTop || scroll.current.top
    const scrollLeft = target.scrollLeft || scroll.current.left

    scroll.current = { top: scrollTop, left: scrollLeft }
  }

  const isPage = artboard?.isPage ?? false

  /**
   * Adding a key to prevent the case where the artboard changes but the
   * image component does not refresh in time, resulting in a glitch
   * where you could see the previous artboard thumbnail when navigating back and forth
   **/
  const artboardImageKey = artboard?.uuid

  const getThumbnail = () => {
    if (artboardThumbnail !== '') {
      return artboardThumbnail
    }

    return artboard
      ? getArtboardThumbnail(artboard as ArtboardDetailInfoFragment)
      : undefined
  }

  return (
    <Container
      id="ArtboardView" // used to get the scroll position for the ContextMenu
      data-testid="artboardview"
      ref={containerRef}
      cursor={artboardContainerCursor({
        inspectorActive: inspector.isActive,
        zoom: currentZoom,
        zoomToFitFactor,
        isMovingAnnotation: Boolean(movingAnnotation),
        isPlacingAnnotation: placeDraftAnnotation,
      })}
      onClick={handleContainerClick}
      onMouseUp={handleContainerMouseUp}
      onScroll={handleScroll}
      {...rest}
    >
      <ScrollArea
        itemWidth={artboardSize.width}
        itemHeight={artboardSize.height}
        containerWidth={containerSize.width}
        containerHeight={containerSize.height}
      >
        <ArtboardContainer
          data-testid="artboard"
          data-artboard-uuid={artboard?.uuid}
          ref={artboardImageRef}
          onClick={event => {
            if (placeDraftAnnotation) {
              handleDraftAnnotationEvent(event.nativeEvent)
              event.stopPropagation()
            }
          }}
          isPage={isPage}
          width={scaledArtboardSize.width}
          height={scaledArtboardSize.height}
        >
          {inspector.data && (
            <>
              <OverlayGrid />
              <OverlayLayout
                artboardHeight={scaledArtboardSize.height}
                artboardWidth={scaledArtboardSize.width}
              />
            </>
          )}
          <ArtboardImage
            key={artboardImageKey}
            alt={artboard?.name ?? ''}
            isPage={isPage}
            zoomFactor={currentZoom.factor}
            src={files?.[0]?.url ?? undefined}
            thumbnail={getThumbnail()}
            height={scaledArtboardSize.height}
            width={scaledArtboardSize.width}
            srcSet={createSrcSetForFiles(files) ?? undefined}
            style={{
              cursor: placeDraftAnnotation
                ? 'copy'
                : artboardContainerCursor({
                    inspectorActive: inspector.isActive,
                    zoom: currentZoom,
                    zoomToFitFactor,
                    isMovingAnnotation: Boolean(movingAnnotation),
                  }),
            }}
          />

          {!IS_EMBEDDED && artboard && (
            <DraftCommentProvider>
              <ArtboardAnnotationOverlay
                shareIdentifier={shareIdentifier}
                permanentArtboardId={artboard?.uuid || ''}
                artboardWidth={artboard?.width || 0}
                artboardHeight={artboard?.height || 0}
              />
            </DraftCommentProvider>
          )}

          {inspector.isActive && (
            <ArtboardInspector
              zoomFactor={currentZoom.factor ?? 1}
              inspector={inspector}
              shareIdentifier={shareIdentifier}
              permanentArtboardShortId={permanentArtboardShortId}
            />
          )}
        </ArtboardContainer>
      </ScrollArea>
      {isValidPagination && (
        <ArtboardPaginationControls
          current={pagination!.current!}
          total={pagination!.total!}
          onlyKeyboardNavigation
        />
      )}
    </Container>
  )
}

export default withRouter(ArtboardView)
